/**
 * Portal global search — Step 84 (Kai: "the requester should have a global
 * search for the things they have access to"). Same grouped-dropdown anatomy
 * as the agent omnisearch (mounted as the portal home's HERO box), scoped
 * to the requester's reach: THEIR requests
 * and the live catalogue — support articles join as one more group when the
 * knowledge base lands. The scoping itself is server-side (session principal
 * + org walls); this box could not widen it if it tried.
 */
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { BookOpen, LayoutGrid, Search, Ticket } from 'lucide-react';
import { portalApi } from './api';
import { CatalogueIcon } from '../fields/catalogueIcons';
import { Highlight } from '../../components/ui/highlight';
import { qk } from '../../lib/queryClient';
import { cn } from '../../lib/utils';

export function PortalSearch({ large = false }: { large?: boolean }) {
  const navigate = useNavigate();
  const inputRef = useRef<HTMLInputElement>(null);
  const rootRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  // URL state on purpose: the portal header's search box hands its term to the
  // results PAGE, which does own a URL. Mirroring it here would fight that.
  const [q, setQ] = useState('');
  const [debounced, setDebounced] = useState('');

  useEffect(() => {
    const t = setTimeout(() => setDebounced(q.trim()), 250);
    return () => clearTimeout(t);
  }, [q]);

  useEffect(() => {
    function onDown(e: MouseEvent) {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    }
    window.addEventListener('mousedown', onDown);
    return () => window.removeEventListener('mousedown', onDown);
  }, []);

  const { data } = useQuery({
    queryKey: qk.portalSearch(debounced),
    queryFn: () => portalApi.search(debounced),
    enabled: open && debounced.length >= 2,
  });

  function go(path: string) {
    setOpen(false);
    setQ('');
    navigate(path);
  }

  const show = open && debounced.length >= 2 && !!data;
  const hasAny =
    (data?.tickets?.total ?? 0) > 0 || (data?.items?.total ?? 0) > 0 || (data?.articles?.total ?? 0) > 0;

  return (
    <div ref={rootRef} className={cn('relative w-full', large ? 'max-w-2xl' : 'max-w-sm')}>
      <Search
        className={cn(
          'pointer-events-none absolute top-1/2 -translate-y-1/2 text-muted-foreground',
          large ? 'left-3.5 h-5 w-5' : 'left-2.5 h-4 w-4',
        )}
      />
      <input
        ref={inputRef}
        value={q}
        onChange={(e) => {
          setQ(e.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        onKeyDown={(e) => {
          if (e.key === 'Escape') {
            setOpen(false);
            inputRef.current?.blur();
          }
          if (e.key === 'Enter') {
            // The LIVE value, not the 250ms-stale debounce (review finding).
            const live = q.trim();
            if (live.length >= 2) {
              setOpen(false);
              setQ('');
              navigate(`/portal/search?q=${encodeURIComponent(live)}`);
            }
          }
        }}
        placeholder={large ? 'Search your requests, services and help…' : 'Search…'}
        className={cn(
          'w-full rounded-md border bg-background text-foreground',
          large ? 'h-12 pl-11 pr-4 text-base shadow-sm' : 'h-9 pl-8 pr-3 text-sm',
          'placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
        )}
      />
      {show && (
        <div className="absolute left-0 right-0 top-full z-40 mt-1 max-h-[60vh] overflow-y-auto rounded-md border bg-popover p-2 shadow-md">
          {!hasAny && (
            <p className="px-2 py-3 text-sm text-muted-foreground">No matches for “{debounced}”.</p>
          )}
          {(data?.tickets?.total ?? 0) > 0 && (
            <div className="mb-2 last:mb-0">
              <p className="mb-1 flex items-center gap-1.5 px-1 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                <Ticket className="h-3.5 w-3.5" /> My requests
              </p>
              <div className="rounded-md border">
                {data!.tickets!.entries.map((t) => (
                  <button
                    key={t.id}
                    className="block w-full border-b px-2.5 py-2 text-left text-sm last:border-b-0 hover:bg-accent"
                    onClick={() => go(`/portal/requests/${t.id}`)}
                  >
                    <Highlight text={t.subject} term={debounced} />{' '}
                    <span className="text-muted-foreground">
                      (#{t.type === 'service_request' ? 'SR' : 'INC'}-{t.number})
                    </span>
                  </button>
                ))}
                {data!.tickets!.total > 3 && (
                  <button
                    className="block w-full px-2.5 py-2 text-left text-sm font-medium text-brand hover:bg-accent"
                    onClick={() => go(`/portal/search?q=${encodeURIComponent(debounced)}&tab=tickets`)}
                  >
                    View all ({data!.tickets!.total})
                  </button>
                )}
              </div>
            </div>
          )}
          {(data?.articles?.total ?? 0) > 0 && (
            <div className="mb-2 last:mb-0">
              <p className="mb-1 flex items-center gap-1.5 px-1 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                <BookOpen className="h-3.5 w-3.5" /> Help articles
              </p>
              <div className="rounded-md border">
                {data!.articles!.entries.map((a) => (
                  <button
                    key={a.id}
                    className="flex w-full items-center gap-2 border-b px-2.5 py-2 text-left text-sm last:border-b-0 hover:bg-accent"
                    onClick={() => go(`/portal/help/${a.slug}`)}
                  >
                    <BookOpen className="h-4 w-4 shrink-0 text-muted-foreground" />
                    <Highlight text={a.title} term={debounced} />
                  </button>
                ))}
                {data!.articles!.total > 3 && (
                  <button
                    className="block w-full px-2.5 py-2 text-left text-sm font-medium text-brand hover:bg-accent"
                    onClick={() => go(`/portal/search?q=${encodeURIComponent(debounced)}&tab=articles`)}
                  >
                    View all ({data!.articles!.total})
                  </button>
                )}
              </div>
            </div>
          )}
          {(data?.items?.total ?? 0) > 0 && (
            <div className="mb-2 last:mb-0">
              <p className="mb-1 flex items-center gap-1.5 px-1 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                <LayoutGrid className="h-3.5 w-3.5" /> Services
              </p>
              <div className="rounded-md border">
                {data!.items!.entries.map((it) => (
                  <button
                    key={it.id}
                    className="flex w-full items-center gap-2 border-b px-2.5 py-2 text-left text-sm last:border-b-0 hover:bg-accent"
                    onClick={() => go(`/portal/catalogue/${it.id}`)}
                  >
                    <CatalogueIcon name={it.icon} className="h-4 w-4 shrink-0 text-brand" />
                    <Highlight text={it.name} term={debounced} />
                  </button>
                ))}
                {data!.items!.total > 3 && (
                  <button
                    className="block w-full px-2.5 py-2 text-left text-sm font-medium text-brand hover:bg-accent"
                    onClick={() => go(`/portal/search?q=${encodeURIComponent(debounced)}&tab=items`)}
                  >
                    View all ({data!.items!.total})
                  </button>
                )}
              </div>
            </div>
          )}
          {hasAny && (
            <button
              className="mt-1 block w-full rounded px-2 py-2 text-left text-sm text-muted-foreground hover:bg-accent hover:text-foreground"
              onClick={() => go(`/portal/search?q=${encodeURIComponent(debounced)}`)}
            >
              See more results for “{debounced}” »
            </button>
          )}
        </div>
      )}
    </div>
  );
}
